import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserRole, LoginCredentials } from '../../types/auth';
import { AuthService } from '../../services/AuthService';

interface AuthContextType {
  user: User | null;
  role: UserRole | null;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => void;
  switchRole: (role: UserRole) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const DummyAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const initAuth = () => {
      const currentUser = AuthService.getCurrentUser();
      setUser(currentUser);
      setLoading(false);
    };
    initAuth();
  }, []);

  const handleLogin = async (credentials: LoginCredentials): Promise<User> => {
    const authenticatedUser = await AuthService.login(credentials);
    setUser(authenticatedUser);
    return authenticatedUser;
  };

  const handleLogout = () => {
    AuthService.logout();
    setUser(null);
  };

  const handleSwitchRole = async (targetRole: UserRole) => {
    const allUsers = await AuthService.getUsers();
    const targetUser = allUsers.find((u) => u.role === targetRole);
    if (targetUser) {
      await handleLogin({ email: targetUser.email, password: targetUser.password });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role: user ? user.role : null,
        isAuthenticated: !!user,
        login: handleLogin,
        logout: handleLogout,
        switchRole: handleSwitchRole,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a DummyAuthProvider');
  }
  return context;
};
